import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../i18n/useP';
import { usePersistentValue } from '../../hooks/usePersistentState';
import { useCockpitData, buildCockpitKpis } from './cockpitCfoModel';
import { Waterfall } from './charts';
import Card from '../../components/Card';
import FinancialsWorkingCapitalTab from '../FinancialsWorkingCapitalTab';
import KpiCard from './KpiCard';
import KpiDrawer from './KpiDrawer';
import './CockpitCfo.css';

/* Cockpit CFO — corps des vues Rentabilité / Liquidité & solidité. Master-détail :
   colonne gauche = cartes KPI + une grande tuile graphique clé ; colonne droite
   fixe (50 %) = au repos les « Signaux clés » + un graphe de synthèse, au clic
   d'une carte son détail (sans superposition). La vue active (`view`) est pilotée
   par les onglets du hub (CfoHub) ; Focus+ et Financement sont des onglets à part.
   Mode édition pour activer/masquer des cartes. */
export default function CockpitCfo({ activeTenant, year, unit = 'kchf', view = 'rent', editMode = false, onGoTab }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id || null;
  const { m, fm, creditLimit, loading } = useCockpitData(tenantId, year);

  const [open, setOpen] = useState(null);
  const [hidden, setHidden] = usePersistentValue(`cockpit_hidden_${tenantId}`, {});

  // Changer d'onglet (rent ↔ liq) ou entrer/sortir du mode édition referme le détail.
  useEffect(() => { setOpen(null); }, [view, editMode]);

  const kpis = useMemo(() => buildCockpitKpis({ m, fm, creditLimit }), [m, fm, creditLimit]);

  if (loading) return <p style={{ color: 'var(--color-text-muted)' }}>{t('phrases:p_2750a21633')}</p>;
  if (!kpis) return <p style={{ color: 'var(--color-text-muted)' }}>{t('phrases:p_ddaa626f04')}</p>;

  const div = unit === 'chf' ? 1 : 1000;
  const unitNote = <span className="ckpit__unit-note">{unit === 'chf' ? 'CHF' : 'kCHF'}</span>;
  const byKey = (k) => kpis.find((x) => x.key === k);
  const groupKey = view === 'rent' ? 'rent' : 'cash';
  // Masqué = choix explicite de l'utilisateur, sinon défaut du KPI (defaultHidden).
  const isHidden = (k) => (k.key in hidden ? hidden[k.key] : !!k.defaultHidden);
  const cards = kpis.filter((k) => k.group === groupKey && (editMode || !isHidden(k)));
  const openKpi = open ? byKey(open) : null;

  function clickCard(k) {
    if (editMode) { setHidden({ ...hidden, [k.key]: !isHidden(k) }); return; }
    setOpen((cur) => (cur === k.key ? null : k.key));
  }
  function closeDetail() { setOpen(null); }

  const ebKpi = byKey('eb');
  const chartFallback = <div style={{ padding: '18px 0', fontSize: 12.5, color: 'var(--color-text-muted)' }}>{p('Série indisponible.')}</div>;

  // Grande tuile graphique clé (bas de la colonne gauche), selon la vue. Pour la
  // liquidité, on réutilise le graphique « Suivi de la liquidité » de l'onglet
  // Suivi des liquidités (mode graphique seul) — plus lisible.
  const leftTile = view === 'rent'
    ? (
      <Card title={t('phrases:p_4dec589873')} actions={unitNote}>
        {ebKpi?.cascade ? <Waterfall steps={ebKpi.cascade} div={div} /> : chartFallback}
      </Card>
    )
    : (
      <FinancialsWorkingCapitalTab section="synthesis-chart" activeTenant={activeTenant} year={year} unit={unit} />
    );


  // Signaux clés COHÉRENTS par vue : Rentabilité → signaux de rentabilité (CA/VA/
  // marge/EBITDA) ; Liquidité & solidité → signaux de liquidité/santé (trésorerie,
  // DSO, liquidité générale, ligne de crédit) issus du moteur.
  const money = (v) => (v == null || !Number.isFinite(v) ? '—'
    : `${Math.round(v / div).toLocaleString('de-CH')} ${unit === 'chf' ? 'CHF' : 'kCHF'}`);
  const pctVs = (a, b) => (b != null && Math.abs(b) > 1e-9
    ? `${(a - b) >= 0 ? '+' : ''}${Math.round(((a - b) / Math.abs(b)) * 100)} %` : '—');
  const cc = m.closed || 12;
  const caK = byKey('ca'); const ebK = byKey('eb');
  const MONTHS_FR = Array.from({ length: 12 }, (_, i) => t(`common:months.${i + 1}`));
  const lastMonthFr = MONTHS_FR[Math.max(0, Math.min(11, cc - 1))];
  const caYtd = caK?.ytd;
  const revM25 = caK?.m2025;
  const caYtdN1 = Array.isArray(revM25) ? revM25.slice(0, cc).reduce((a, b) => a + (Number(b) || 0), 0) : null;
  const caFy25 = Array.isArray(revM25) ? revM25.reduce((a, b) => a + (Number(b) || 0), 0) : null;
  const ebYtd = ebK?.ytd; const ebFy = ebK?.proj; const eb2025 = ebK?.n1;
  const margin = (ebYtd != null && caYtd) ? (ebYtd / caYtd * 100).toFixed(1).replace('.', ',') : null;
  const margin25 = (eb2025 != null && caFy25) ? (eb2025 / caFy25 * 100).toFixed(1).replace('.', ',') : null;
  // C3 — CA net : YTD (à fin du dernier mois clôturé) vs YTD même période N-1.
  const caSignal = (caYtd != null && caYtdN1 != null)
    ? `${p("Chiffre d'affaires net")} : ${money(caYtd)} vs ${money(caYtdN1)} — ${p('cumulé à fin')} ${lastMonthFr} vs ${year - 1} ${p('même période')} · ${pctVs(caYtd, caYtdN1)}` : null;
  // C4 — EBITDA : absolu + marge (% du CA) + marge moyenne N-1 en comparaison
  // (le % de variation sur base ~0 étant ininterprétable).
  const ebSignal = (ebYtd != null)
    ? `EBITDA : ${money(ebYtd)} ${p('à ce jour')}${margin != null ? ` · ${margin} ${p('% du CA')}` : ''}`
      + `${margin25 != null ? ` · ${p('marge moyenne')} ${year - 1} : ${margin25} %` : ''}` : null;
  // Valeur ajoutée : productivité VA/EPT (annualisée) vs moyenne N-1 — cohérent
  // avec le drawer (VA/EPT), insensible au mix marchandises.
  const vaK = byKey('va');
  const vaSignal = (vaK?.vaEptAnn != null)
    ? `${p('Valeur ajoutée par EPT')} : ${money(vaK.vaEptAnn)}${vaK.vaEptAnnN1 != null ? ` · ${p('moyenne')} ${year - 1} : ${money(vaK.vaEptAnnN1)} · ${pctVs(vaK.vaEptAnn, vaK.vaEptAnnN1)}` : ''}` : null;
  // Marge brute : % du CA vs moyenne N-1 + sensibilité (1 pt de marge = combien
  // d'EBITDA, base CA net N-1) → mesure l'enjeu d'un point perdu/gagné.
  const mbK = byKey('mb');
  const fmt1 = (v) => v.toFixed(1).replace('.', ',');
  const mb26num = (mbK?.ytd != null && caYtd) ? (mbK.ytd / caYtd * 100) : null;
  const mb25num = (mbK?.n1 != null && caFy25) ? (mbK.n1 / caFy25 * 100) : null;
  const onePt = caFy25 ? caFy25 * 0.01 : null;                 // valeur d'1 pt de marge (CHF, base N-1)
  const gapPts = (mb26num != null && mb25num != null) ? (mb26num - mb25num) : null;
  const ecartChf = (gapPts != null && onePt != null) ? gapPts * onePt : null;
  const mbSignal = (mb26num != null)
    ? `${p('Marge brute')} : ${fmt1(mb26num)} ${p('% du CA')}${mb25num != null ? ` · ${p('moyenne')} ${year - 1} : ${fmt1(mb25num)} %` : ''}`
      + `${gapPts != null ? ` · ${p('écart')} ${gapPts >= 0 ? '+' : '−'}${fmt1(Math.abs(gapPts))} pt ≈ ${money(ecartChf)}` : ''}`
      + `${onePt != null ? ` (1 pt = ${money(onePt)})` : ''}` : null;
  // Rendement masse salariale (VA ÷ personnel) vs moyenne N-1 + référence.
  const rendK = byKey('ratioept');
  const rendSignal = (rendK?.ytd != null)
    ? `${p('Rendement masse salariale')} : ${rendK.ytd.toFixed(2)}×${rendK.n1 != null ? ` · ${p('moyenne')} ${year - 1} : ${rendK.n1.toFixed(2)}×` : ''} · ${p('réf 1,3×')}` : null;
  // Effet ciseaux : croissance revenus vs charges + impact EBIT chiffré.
  const fmtGrowth = (v) => `${v >= 0 ? '+' : '−'}${Math.abs(v * 100).toFixed(0)} %`;
  const cisK = byKey('ciseaux');
  const cisSignal = (cisK?.revG != null && cisK?.chgG != null)
    ? `${p('Évolution des charges')} : ${p('revenus')} ${fmtGrowth(cisK.revG)} vs ${p('charges')} ${fmtGrowth(cisK.chgG)}${cisK.ebitImpact != null ? ` · ${p('impact EBIT ≈')} ${money(cisK.ebitImpact)} ${p('sur 12 mois')}` : ''}` : null;
  const rentSignals = [caSignal, vaSignal, mbSignal, ebSignal, rendSignal, cisSignal].filter(Boolean);

  // ── Signaux Liquidité (ordre imposé) ──────────────────────────────────────
  const num1 = (v) => v.toFixed(1).replace('.', ',');
  const trK = byKey('tr'); const burnK = byKey('burn');
  const treso = trK?.ytd; const tresoN1 = trK?.n1;
  // 1) Variation de trésorerie depuis le 31.12 N-1 (cumulé YTD).
  const trVarYtd = (treso != null && tresoN1 != null) ? treso - tresoN1 : null;
  const sigTrVar = trVarYtd != null
    ? `${p('Variation de trésorerie depuis le 31.12.')}${year - 1} : <strong>${trVarYtd >= 0 ? '+' : ''}${money(trVarYtd)}</strong>` : null;
  // 2) Trésorerie en mois de salaire (existant).
  const sigMonthsSal = (m.monthsSalary != null)
    ? `${p('Trésorerie =')} <strong>${num1(m.monthsSalary)} ${p('mois')}</strong> ${p('de salaire')}${m.monthsSalary < 2 ? ' (&lt; 2)' : ''}` : null;
  // 3) Burn rate + signification runway.
  const sigBurn = (burnK?.ytd != null)
    ? `${p('Burn rate')} <strong>${money(Math.abs(burnK.ytd))}${p('/mois')}</strong>${m.runway != null ? ` · ${p('runway')} ${num1(m.runway)} ${p('mois')} ${p('avant épuisement de la trésorerie')}` : ''}` : null;
  // 4) Quick ratio : marge de sécurité (ou manque) en CHF jusqu'à 1,0×.
  const marginChf = (m.r2 != null && m.curLiab != null) ? (m.r2 - 1) * Math.abs(m.curLiab) : null;
  const sigQuick = (m.r2 != null)
    ? (m.r2 >= 1
        ? `${p('Liquidité réduite')} ${m.r2.toFixed(2)}× : ${p('marge de sécurité')} <strong>${money(marginChf)}</strong> ${p('au-dessus de 1,0×')}`
        : `${p('Liquidité réduite')} ${m.r2.toFixed(2)}× : ${p('il manque')} <strong>${money(Math.abs(marginChf))}</strong> ${p('pour atteindre 1,0×')}`) : null;
  // 5) BFRE : niveau, part du CA net (12 m glissant) et variation depuis le début d'année.
  const openBfre = fm?.wc?.openBfre;
  const bfreVar = (m.bfre != null && openBfre != null) ? m.bfre - openBfre : null;
  const caBase = m.ca12m || null;
  const bfrePct = (m.bfre != null && caBase) ? (m.bfre / caBase * 100) : null;
  const sigBfre = (m.bfre != null)
    ? `BFRE <strong>${money(m.bfre)}</strong>${bfrePct != null ? ` · ${num1(bfrePct)} ${p('% du CA net (12 m)')}` : ''}${bfreVar != null ? ` · ${bfreVar >= 0 ? '+' : ''}${money(bfreVar)} ${p('depuis le 31.12')}` : ''}` : null;
  const liqSignals = [sigTrVar, sigMonthsSal, sigBurn, sigQuick, sigBfre].filter(Boolean);
  const shownSignals = view === 'rent' ? rentSignals : liqSignals;

  return (
    <div className="ckpit">
      {/* Indice de la vue. Le bouton « Modifier le cockpit » est remonté au niveau
          des onglets du hub (CfoHub) ; ici on ne garde que l'indice. */}
      <div className="ckpit__viewbar">
        <span className="ckpit__hint">{editMode ? p('Activez ou masquez les indicateurs') : p('Cliquez un indicateur pour creuser')}</span>
      </div>

      {/* Corps master-détail */}
      <div className="ckpit__body">
        <div className="ckpit__left">
          <div className="ckpit__cards">
            {cards.map((k) => (
              <KpiCard
                key={k.key} kpi={k} unit={unit}
                active={!editMode && open === k.key}
                editMode={editMode} hidden={isHidden(k)}
                onClick={() => clickCard(k)}
              />
            ))}
          </div>
          {leftTile}
        </div>

        <div className="ckpit__right">
          {openKpi && !editMode ? (
            <KpiDrawer
              key={openKpi.key} kpi={openKpi} unit={unit}
              onClose={closeDetail}
              onGoTab={(tab) => { if (onGoTab) onGoTab(tab); closeDetail(); }}
            />
          ) : (
            <Card key={`repos-${view}`} className="ckpit__panel ckpit__panel--ghost" title={t('phrases:p_d1782d0ad0')} actions={unitNote}>
              {shownSignals.length > 0 ? (
                <ul className="ckpit__signals">
                  {shownSignals.map((s, i) => (
                    <li className="ckpit__signal" key={`s${i}`} dangerouslySetInnerHTML={{ __html: s }} />
                  ))}
                </ul>
              ) : (
                <p className="ckpit__nosignal">{t('phrases:p_c9a4f1798d')}</p>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
